<template>
    <vx-page-layout title="Integration Logs">
        <template v-slot:subtitle>
            <a href="/settings/relay" class="no-underline! text-sky-600! text-base font-normal">
                <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                All Integrations
            </a>
        </template>

        <div class="flex flex-wrap gap-4 mb-6">
            <div class="w-48" data-test="status-filter">
                <vx-label>Status</vx-label>
                <vx-selectable
                    v-model="filters.status"
                    :options="statusOptions"
                    :reduce="(opt: SelectableOption) => opt.value"
                    label="label"
                    :clearable="false"
                />
            </div>
            <div class="w-48" data-test="date-range-filter">
                <vx-label>Date Range</vx-label>
                <vx-selectable
                    v-model="filters.date_range"
                    :options="dateRangeOptions"
                    :reduce="(opt: SelectableOption) => opt.value"
                    label="label"
                    :clearable="false"
                />
            </div>
        </div>

        <div v-if="loading && !list.length" class="py-8 flex flex-col items-center gap-2">
            <vx-icon-loading class="w-8 h-8" spin></vx-icon-loading>
            <span class="text-sm text-slate-400">Results may take a few seconds to load</span>
        </div>

        <vx-alert v-else-if="error" color="danger">
            {{ error }}
        </vx-alert>

        <vx-box
            color="info"
            v-else-if="!list.length"
            class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
        >
            No integration logs found.
        </vx-box>

        <template v-else>
            <vx-table class="md:grid-cols-4-auto" rowBreakpoint="md">
                <vx-table-row isHeader>
                    <div>Status</div>
                    <div>Received</div>
                    <div>Vendor</div>
                    <div></div>
                </vx-table-row>

                <integration-log-row v-for="item in list" :key="item.ptr ?? undefined" :item="item" />
            </vx-table>

            <div v-if="canLoadMore" class="mt-8">
                <vx-button
                    @click="loadMore()"
                    color="muted-dark"
                    size="xl"
                    class="grow-0"
                    :loading="loading"
                    data-test="load-more-btn"
                >
                    Load More
                </vx-button>
            </div>
        </template>
    </vx-page-layout>
</template>

<script lang="ts" setup>
import {
    VxTable,
    VxTableRow,
    VxBox,
    VxButton,
    VxIconLoading,
    VxSelectable,
    VxLabel,
    VxAlert,
    VxPageLayout,
} from '@voxie/frontend-components';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import IntegrationLogRow from './IntegrationLogRow.vue';
import { integrationLogsClient, type IntegrationLogListItem } from '~/services/integrationLogsClient';
import { computed, onMounted, reactive, ref, watch } from 'vue';

const props = defineProps<{
    vendor?: string;
}>();

const MAX_RESULTS = 200;

interface SelectableOption {
    label: string;
    value: string | undefined;
}

const teamId = Spark.state.currentTeam.id;

const statusOptions: SelectableOption[] = [
    { label: 'All', value: undefined },
    { label: 'Success', value: 'success' },
    { label: 'Error', value: 'error' },
];

const dateRangeOptions: SelectableOption[] = [
    { label: 'Last hour', value: '1h' },
    { label: 'Last 24 hours', value: '24h' },
    { label: 'Last 7 days', value: '7d' },
];

const filters = reactive({
    status: undefined as string | undefined,
    date_range: '1h' as string | undefined,
});

const list = ref<IntegrationLogListItem[]>([]);
const cursor = ref<string | undefined>(undefined);
const loading = ref(false);
const error = ref<string | null>(null);
const canLoadMore = computed(() => !!cursor.value && list.value.length < MAX_RESULTS);

let abortController: AbortController | null = null;

async function fetchLogs(appendCursor?: string): Promise<void> {
    if (abortController) {
        abortController.abort();
    }
    abortController = new AbortController();
    const signal = abortController.signal;

    loading.value = true;
    error.value = null;

    try {
        const response = await integrationLogsClient.getLogs(teamId, {
            vendor: props.vendor,
            status: filters.status,
            date_range: filters.date_range,
            cursor: appendCursor ?? null,
        });

        if (signal.aborted) {
            return;
        }

        const result = response.data;

        if (result.timed_out) {
            error.value = 'Query timed out. Try a smaller date range.';
            return;
        }

        if (appendCursor) {
            list.value = [...list.value, ...result.data];
        } else {
            list.value = result.data;
        }
        cursor.value = result.next_cursor;
    } catch {
        if (!signal.aborted) {
            error.value = 'An error occurred. Please try again.';
        }
    } finally {
        if (!signal.aborted) {
            loading.value = false;
        }
    }
}

function loadMore(): void {
    if (cursor.value) {
        fetchLogs(cursor.value);
    }
}

watch(filters, () => {
    if (abortController) {
        abortController.abort();
    }
    list.value = [];
    cursor.value = undefined;
    fetchLogs();
});

onMounted(() => {
    fetchLogs();
});
</script>
